import { LegalPage } from "../../components/LegalPage";

export function LicensingMethodology() {
  return (
    <LegalPage
      title="Licensing & CE methodology"
      eyebrow="Trust center"
      description="How WeConsult Rx tracks pharmacist state licenses and continuing education credits. Self-report MVP, partner integrations roadmap (NABP CPE Monitor + CE Broker), per-state subject-mandate rule sourcing, and the customer's responsibility to verify with their state board."
      path="/trust/licensing-methodology"
      effective="2026-05-31"
    >
      <p>
        WeConsult Rx provides a license + CE tracking surface so consultant
        pharmacists can manage state licensure, CE compliance, and renewal
        deadlines inside the application they already use for chart review.
        This page documents how we source data, where automation begins and
        ends, and what remains the licensee's responsibility.
      </p>

      <h2>Data sources</h2>
      <p>
        Today the platform supports three sources for license + CE records,
        in order of increasing assurance:
      </p>
      <ul>
        <li>
          <strong>Self-report</strong> — a pharmacist enters license number,
          state, expiration date, and logs each CE activity manually. Records
          carry the badge "Self-reported" and are never marked verified.
        </li>
        <li>
          <strong>PDF upload</strong> — certificates uploaded as PDF are
          stored in a tenant-scoped bucket. A best-effort parser extracts the
          ACPE Universal Activity Number (UAN), provider, hours, and completion
          date. The user reviews and confirms before the record is saved.
        </li>
        <li>
          <strong>Partner sync</strong> — NABP CPE Monitor and CE Broker
          integrations are scaffolded in the platform and will set
          <code>verified=true</code> automatically once partner API access is
          provisioned. Until then, the "Verify with NABP" CTA is visible but
          disabled and the partner adapter routes return a provisional
          response.
        </li>
      </ul>

      <h2>NABP CPE Monitor partnership</h2>
      <p>
        WeConsult Rx is pursuing enrollment in the NABP CPE Monitor Partner
        API program. When access lands, pharmacists with linked NABP
        e-Profiles will see their CE history populate automatically and
        records will carry the "NABP-verified" badge. No code changes will
        affect the pharmacist UX — only the partner adapter body is swapped.
      </p>

      <h2>CE Broker partnership</h2>
      <p>
        For states that use CE Broker as their official tracking system (e.g.
        FL, GA, KY, NC, TN), we will additionally support direct sync once
        partner credentials are issued. Until then, CE Broker-tracked records
        can be entered manually or via PDF upload.
      </p>

      <h2>Per-state subject mandates</h2>
      <p>
        The compliance compute engine reads from
        <code>ce_state_requirements</code> — a curated table of total hours,
        renewal cycle length, and subject-mandate rules per state. Each row
        cites the state board CE rule page used to source the values and the
        date last reviewed.
      </p>
      <p>
        We seeded the top 10 states by pharmacist population at launch (FL,
        TX, CA, NY, OH, PA, IL, GA, NC, MI). Additional states will be added
        as customer demand requires. Where a state's rules are ambiguous or
        actively in flux, the row carries the note
        <code>needs-board-review</code> and the UI surfaces a "Verify with
        your state board" reminder rather than a numerical compliance
        readout.
      </p>
      <p>
        Subject-mandate accuracy: we review state board pages at least
        annually and on any customer-reported change. The
        <code>last_updated</code> column lets pharmacists see staleness at a
        glance.
      </p>

      <h2>Renewal reminders</h2>
      <p>
        A weekly Monday job scans all licenses with expiration dates within
        120 days and emits in-app + email reminders at the 120, 90, 60, 30,
        14, 7, and 1-day marks. Reminders are visible in the inbox and audit
        log; no PHI is ever included in reminder content (only license state
        and expiration date).
      </p>

      <h2>Audit invariants</h2>
      <p>
        Pharmacist license numbers are PII-sensitive (state board public
        verifier surfaces). Audit-log metadata for license events therefore
        carries only state and license type — the full license number is
        stored on the row itself but never in the audit metadata, and never
        in renewal-reminder content.
      </p>
      <p>
        CE record metadata carries hours, hours type, and source — title and
        provider are stored on the row but never logged to audit metadata.
      </p>

      <h2>What remains the licensee's responsibility</h2>
      <p>
        WeConsult Rx is a tracking tool, not a board of pharmacy. Each
        licensed pharmacist remains responsible for:
      </p>
      <ul>
        <li>
          Verifying their CE history with their state board and with NABP's
          official e-Profile system before renewal.
        </li>
        <li>
          Submitting the actual renewal application and fee on time through
          their state board's licensing portal.
        </li>
        <li>
          Maintaining authoritative copies of CE certificates for the
          retention period required by their state (typically 4 to 6 years).
        </li>
        <li>
          Reporting our self-report data accurately. Records marked
          "Self-reported" are not third-party verified.
        </li>
      </ul>

      <h2>Roadmap</h2>
      <ul>
        <li>NABP CPE Monitor Partner API integration — partnership in progress.</li>
        <li>CE Broker partner API integration — partnership in progress.</li>
        <li>State board direct verification for boards that publish an API.</li>
        <li>50-state subject mandate coverage (10 seeded at launch).</li>
        <li>Bedrock-powered PDF certificate parsing with structured-output guard rails.</li>
      </ul>
    </LegalPage>
  );
}
